import { PayloadAction } from '@reduxjs/toolkit'
import { reducerCreatorProducer } from 'store/common'

import {
  IProjectDataState,
  TUsedUserSoundsMetaData,
  IUpdateMediaItemPayload,
  IUpdateAudioEffectPayload
} from 'store/types/projectData'
import {
  IMediaItem,
  MediaStatus,
  TMediaItemType,
  TPartialMediaItem
} from 'types/media'
import { IAudioItem, IUploadedAudioPeaks, TAudioType } from 'types/audio'
import { IProjectData } from 'types/projectData'
import {
  filterExistingAudios,
  mutateExistingAudioItemsInPlace,
  createDuplicatedItem,
  updateProjectDataArray,
  getNewAudioItemsWithAdjustedPosition,
  shiftOverlappedTimelineItems,
  getCurrentLineItems,
  spliceSamplesIntoPendingPlaceholder
} from './utils'
import { IUpdatedTtsItemData } from 'types/text-to-speech'
import { isVisualMediaItem } from 'utils/media'
import { isAudioItem, splitFadeEffects } from 'utils/audio'
import {
  TActiveTimelineItems,
  TActiveTimelineItemType
} from 'store/types/editor'
import { IAddMediaItemsPayload } from 'store/types/projectData'

const appendMediaItems = (
  state: IProjectDataState,
  action: PayloadAction<IAddMediaItemsPayload<IMediaItem>>
) => {
  if (!state.present.mediaItems) {
    state.present.mediaItems = []
  }
  state.present.mediaItems.push(...action.payload.value)
}

const updateMediaItem = (
  state: IProjectDataState,
  action: PayloadAction<IUpdateMediaItemPayload>,
  config: {
    isCheckAssetId?: boolean
  } = { isCheckAssetId: false }
) => {
  const { id, updates } = action.payload
  const mediaItems = state.present.mediaItems
  const itemIndex = mediaItems.findIndex(item => {
    const assetIdCheck = updates.assetId === item.assetId && item.id === id

    if (config.isCheckAssetId && updates.assetId) {
      return assetIdCheck
    }
    return item.id === id
  })
  const updatedItemCurrentDuration = mediaItems[itemIndex]?.duration

  if (itemIndex !== -1) {
    state.present.mediaItems[itemIndex] = {
      ...mediaItems[itemIndex],
      ...updates
    }
  }

  const hasDurationIncreased =
    updates.duration &&
    updatedItemCurrentDuration &&
    updates.duration > updatedItemCurrentDuration

  if (hasDurationIncreased) {
    const editedItemLineIndex = mediaItems[itemIndex].zIndex
    const currentLineMediaItems = mediaItems.filter(
      item => item.zIndex === editedItemLineIndex
    )
    shiftOverlappedTimelineItems({
      itemsArray: currentLineMediaItems,
      itemStartKey: 'startTime',
      itemDurationKey: 'duration',
      processedItemId: id
    })
  }
}

const reducers = reducerCreatorProducer<IProjectDataState>()({
  setProjectData: (state, action: PayloadAction<IProjectData>) => {
    state.present = action.payload
  },
  setDefaultDimensions: (
    state,
    action: PayloadAction<{ width: number; height: number }>
  ) => {
    const { width, height } = action.payload
    state.present.defaultWidth = width
    state.present.defaultHeight = height
  },
  setIsLoading: (state, action: PayloadAction<boolean>) => {
    state.isLoading = action.payload
  },
  addMediaItems: appendMediaItems,
  updateMediaItem,
  updateMediaItems: (
    state,
    action: PayloadAction<IUpdateMediaItemPayload[]>
  ) => {
    const mediaItems = state.present.mediaItems
    for (const { id, updates } of action.payload) {
      const itemIndex = mediaItems.findIndex(item => item.id === id)
      if (itemIndex === -1) continue

      const currentDuration = mediaItems[itemIndex].duration
      mediaItems[itemIndex] = {
        ...mediaItems[itemIndex],
        ...updates
      }

      if (
        updates.duration &&
        currentDuration &&
        updates.duration > currentDuration
      ) {
        const editedItemLineIndex = mediaItems[itemIndex].zIndex
        const currentLineMediaItems = mediaItems.filter(
          item => item.zIndex === editedItemLineIndex
        )
        shiftOverlappedTimelineItems({
          itemsArray: currentLineMediaItems,
          itemStartKey: 'startTime',
          itemDurationKey: 'duration',
          processedItemId: id
        })
      }
    }
  },
  updateMediaItemWithoutUndoSave: (
    state,
    action: PayloadAction<IUpdateMediaItemPayload>
  ) => {
    updateMediaItem(state, action, {
      isCheckAssetId: action.payload?.isCheckAssetId ?? true
    })
  },
  uploadFromMediaLibrary: appendMediaItems,
  setMediaItems: (
    state,
    action: PayloadAction<IAddMediaItemsPayload<IMediaItem>>
  ) => {
    state.present.mediaItems = action.payload.value
  },
  // Finalize avatar generation: replace the PENDING placeholder with the
  // rendered sample clips in the present timeline AND in every past/future
  // snapshot that still holds it, so the finished clips survive undo/redo.
  //
  // aiVideo finalizes 1 placeholder -> 1 clip in place, which the undo
  // middleware propagates into snapshots via an id-keyed merge
  // (`updateProjectDataArray`). Avatar generation is 1 placeholder -> N clips,
  // which changes the array length and can't ride that merge — so we splice the
  // snapshots here directly instead.
  replacePendingPlaceholderWithSamples: (
    state,
    action: PayloadAction<{ id: string; samples: IMediaItem[] }>
  ) => {
    const { id, samples } = action.payload
    if (!samples.length) return

    // FIT each sample into the canvas using that array's own dimensions: the
    // present uses the live canvas, and each past/future snapshot uses its own
    // recorded dimensions (they can differ if the user switched aspect ratio and
    // undid).
    state.present.mediaItems = spliceSamplesIntoPendingPlaceholder(
      state.present.mediaItems,
      id,
      samples,
      { width: state.present.defaultWidth, height: state.present.defaultHeight }
    )
    state.past = state.past.map(projectData => ({
      ...projectData,
      mediaItems: spliceSamplesIntoPendingPlaceholder(
        projectData.mediaItems,
        id,
        samples,
        {
          width: projectData.defaultWidth,
          height: projectData.defaultHeight
        }
      )
    }))
    state.future = state.future.map(projectData => ({
      ...projectData,
      mediaItems: spliceSamplesIntoPendingPlaceholder(
        projectData.mediaItems,
        id,
        samples,
        {
          width: projectData.defaultWidth,
          height: projectData.defaultHeight
        }
      )
    }))
  },
  removeVisualMediaItem: (state, action: PayloadAction<string>) => {
    const idToRemove = action.payload
    state.present.mediaItems = state.present.mediaItems.filter(
      item => item.id !== idToRemove
    )
  },
  splitVisualMediaItem: (
    state,
    action: PayloadAction<{
      splitTime: number
      splittingMediaId: string
    }>
  ) => {
    const { splitTime, splittingMediaId } = action.payload

    const splittingMediaItem = state.present.mediaItems.find(
      item =>
        item.id === splittingMediaId &&
        item.startTime < splitTime &&
        item.startTime + item.duration > splitTime
    )

    if (!splittingMediaItem) return

    const result = []
    for (const item of state.present.mediaItems) {
      if (item.id === splittingMediaItem.id) {
        // The cut creates a fade-less edge: the left half keeps only the
        // fade-in, the right half only the fade-out, each re-clamped to its new
        // (shorter) duration.
        const firstDuration = splitTime - item.startTime
        const secondDuration = item.startTime + item.duration - splitTime
        result.push({
          ...item,
          duration: firstDuration,
          meta: {
            ...item.meta,
            audioEffects: splitFadeEffects(
              item.meta.audioEffects,
              'left',
              firstDuration
            )
          }
        })
        result.push({
          ...item,
          duration: secondDuration,
          startTime: splitTime,
          id: crypto.randomUUID(),
          meta: {
            ...item.meta,
            ...(item.meta.trimStart !== undefined
              ? {
                  trimStart:
                    item.meta.trimStart +
                    (splitTime - item.startTime) *
                      (item.meta.playbackSpeed ?? 1)
                }
              : {}),
            audioEffects: splitFadeEffects(
              item.meta.audioEffects,
              'right',
              secondDuration
            )
          }
        })
      } else {
        result.push(item)
      }
    }
    state.present.mediaItems = result
  },
  splitAudioItem: (
    state,
    action: PayloadAction<{
      splitTime: number
      splittingAudioId: string
    }>
  ) => {
    const { splitTime, splittingAudioId } = action.payload

    const splittingItem = state.present.multilineAudios.find(
      item =>
        item.id === splittingAudioId &&
        item.projectStart < splitTime &&
        item.projectStart + item.duration > splitTime
    )

    if (!splittingItem) return

    const result = []
    for (const item of state.present.multilineAudios) {
      if (item.id === splittingItem.id) {
        // The cut creates a fade-less edge: the left half keeps only the
        // fade-in, the right half only the fade-out, each re-clamped to its new
        // (shorter) duration.
        const firstDuration = splitTime - item.projectStart
        const secondDuration = item.projectStart + item.duration - splitTime
        result.push({
          ...item,
          duration: firstDuration,
          audioEffects: splitFadeEffects(
            item.audioEffects,
            'left',
            firstDuration
          )
        })
        result.push({
          ...item,
          duration: secondDuration,
          projectStart: splitTime,
          mediaStart:
            item.mediaStart +
            (splitTime - item.projectStart) * (item.playbackSpeed ?? 1),
          id: crypto.randomUUID(),
          audioEffects: splitFadeEffects(
            item.audioEffects,
            'right',
            secondDuration
          )
        })
      } else {
        result.push(item)
      }
    }
    state.present.multilineAudios = result
  },
  updateUploadedMediaItems: (
    state,
    action: PayloadAction<
      { id: string; updates: TPartialMediaItem; status?: MediaStatus }[]
    >
  ) => {
    action.payload.forEach(({ id, updates, status }) => {
      const mediaItemIndex = state.present.mediaItems.findIndex(
        item => item.id === id
      )
      const { meta = {}, ...restUpdates } = updates

      if (mediaItemIndex !== -1) {
        state.present.mediaItems[mediaItemIndex] = {
          ...state.present.mediaItems[mediaItemIndex],
          ...restUpdates,
          meta: {
            ...state.present.mediaItems[mediaItemIndex].meta,
            ...meta,
            status
          }
        }
      }
    })
  },
  setMultilineAudios: (state, action: PayloadAction<IAudioItem[]>) => {
    state.present.multilineAudios = action.payload
  },
  addMultiLineAudios: (
    state,
    action: PayloadAction<IAddMediaItemsPayload<IAudioItem>>
  ) => {
    state.present.multilineAudios.push(...action.payload.value)
  },
  updateAudioItems: (
    state,
    action: PayloadAction<{ ids: string[]; updates: Partial<IAudioItem> }>
  ) => {
    const { ids, updates } = action.payload
    const multilineAudios = state.present.multilineAudios
    ids.forEach(id => {
      const itemIndex = multilineAudios.findIndex(item => item.id === id)
      if (itemIndex === -1) {
        return
      }
      const oldDuration = multilineAudios[itemIndex].duration
      multilineAudios[itemIndex] = {
        ...multilineAudios[itemIndex],
        ...updates
      }
      const updatedItem = multilineAudios[itemIndex]
      if (updatedItem.duration > oldDuration) {
        const currentLineItems = getCurrentLineItems(
          multilineAudios,
          updatedItem.lineIndex
        )
        shiftOverlappedTimelineItems({
          itemsArray: currentLineItems,
          itemStartKey: 'projectStart',
          itemDurationKey: 'duration',
          processedItemId: id
        })
      }
    })
  },
  updateUploadedAudioData: (
    state,
    action: PayloadAction<
      {
        id: string
        mediaPath: string
        mediaId: number
        fileSize: number
        peaks?: IUploadedAudioPeaks
      }[]
    >
  ) => {
    if (!state.usedUserSoundsMetaData) {
      state.usedUserSoundsMetaData = {}
    }
    action.payload.forEach(({ id, mediaPath, mediaId, fileSize, peaks }) => {
      const audio = state.present.multilineAudios.find(item => item.id === id)
      if (audio) {
        audio.mediaPath = mediaPath
        audio.assetId = `${mediaId}`
      }
      state.usedUserSoundsMetaData![`${mediaId}`] = {
        fileSize,
        peaks
      }
    })
  },
  updateTtsAudios: (state, action: PayloadAction<IUpdatedTtsItemData[]>) => {
    const generatedTtsAudios = action.payload
    const multilineAudios = state.present.multilineAudios
    const ttsMultilineAudios = multilineAudios.filter(
      audio => audio.type === 'TTS'
    )
    const regeneratedTtsAudios = filterExistingAudios(
      generatedTtsAudios,
      ttsMultilineAudios
    )
    mutateExistingAudioItemsInPlace(regeneratedTtsAudios, ttsMultilineAudios)

    const uniqueLineIndexes = [
      ...new Set(ttsMultilineAudios.map(audio => audio.lineIndex))
    ]
    uniqueLineIndexes.forEach(lineIndex => {
      const lineItems = getCurrentLineItems(multilineAudios, lineIndex)

      shiftOverlappedTimelineItems({
        itemsArray: lineItems,
        itemStartKey: 'projectStart',
        itemDurationKey: 'duration'
      })
    })

    const newAudioItems = getNewAudioItemsWithAdjustedPosition(
      generatedTtsAudios,
      ttsMultilineAudios,
      multilineAudios
    )
    state.present.multilineAudios = [...multilineAudios, ...newAudioItems]
  },

  removeAudiosFromProject: (state, action: PayloadAction<string[]>) => {
    const ids = action.payload
    ids.forEach(id => {
      const index = state.present.multilineAudios.findIndex(
        item => item.id === id
      )
      if (index !== -1) {
        state.present.multilineAudios.splice(index, 1)
      }
    })
  },
  duplicateAudioItem: (state, action: PayloadAction<string>) => {
    const idToDuplicate = action.payload

    const duplicatedItem = createDuplicatedItem(
      state.present.multilineAudios,
      idToDuplicate,
      'lineIndex',
      'projectStart'
    )

    if (duplicatedItem) {
      state.present.multilineAudios.push(duplicatedItem)
    }
  },
  updateActiveAudioVolume: (
    state,
    action: PayloadAction<{ volume: number; activeAudioIds: string[] }>
  ) => {
    const { volume, activeAudioIds } = action.payload
    state.present.multilineAudios.forEach(item => {
      if (activeAudioIds.includes(item.id)) {
        item.volume = volume
      }
    })
  },
  updateAudioItemData: (state, action: PayloadAction<IAudioItem>) => {
    const audios = state.present.multilineAudios
    const audio = action.payload
    const targetIndex = state.present.multilineAudios.findIndex(
      item => item.id === audio.id
    )
    const oldDuration = state.present.multilineAudios[targetIndex].duration
    if (targetIndex !== -1) {
      state.present.multilineAudios[targetIndex] = audio
    }
    // Handle collision detection if duration increased
    if (audio.duration > oldDuration) {
      const currentLineItems = getCurrentLineItems(audios, audio.lineIndex)

      shiftOverlappedTimelineItems({
        itemsArray: currentLineItems,
        itemStartKey: 'projectStart',
        itemDurationKey: 'duration',
        processedItemId: audio.id
      })
    }
  },
  saveToPast: (state, action: PayloadAction<IProjectData>) => {
    state.future = []
    state.past.push(action.payload)

    // Limit past array to prevent memory issues (keep last 50 states)
    if (state.past.length > 50) {
      state.past = state.past.slice(-50)
    }
  },
  undo: state => {
    if (state.past.length > 0) {
      // Move current present to future
      state.future.unshift(state.present)

      // Restore the last state from past
      const lastPastState = state.past.pop()!
      state.present = lastPastState
    }
  },
  redo: state => {
    if (state.future.length > 0) {
      // Move current present to past
      state.past.push(state.present)

      // Restore the first state from future
      const firstFutureState = state.future.shift()!
      state.present = firstFutureState
    }
  },
  updateUndoRedoMediaItems: (
    state,
    action: PayloadAction<
      {
        id: string
        updates: TPartialMediaItem
        status: MediaStatus
      }[]
    >
  ) => {
    const payload = action.payload

    state.past = updateProjectDataArray(state.past, payload)

    state.future = updateProjectDataArray(state.future, payload)
  },
  updateUndoRedoAudioItems: (
    state,
    action: PayloadAction<{ mediaPath: string; mediaId: number }[]>
  ) => {
    const payload = action.payload

    state.present.multilineAudios.forEach(item => {
      const updatedItem = payload.find(
        audio => audio.mediaId === Number(item.assetId)
      )
      if (updatedItem) {
        item.mediaPath = updatedItem.mediaPath
      }
    })
  },
  removeTimelineSelectedItems: (
    state,
    action: PayloadAction<TActiveTimelineItems>
  ) => {
    const items = action.payload
    const audioItems = items.filter(item => isAudioItem(item.type))
    const visualMediaItems = items.filter(item => isVisualMediaItem(item.type))

    state.present.multilineAudios = state.present.multilineAudios.filter(
      item =>
        !audioItems.some(
          audio => audio.id === item.id && audio.type === item.type
        )
    )
    state.present.mediaItems = state.present.mediaItems.filter(
      item =>
        !visualMediaItems.some(
          media => media.id === item.id && media.type === item.type
        )
    )
  },
  updateTimelineItems: (
    state,
    action: PayloadAction<
      { id: string | null; type: TActiveTimelineItemType; left: number }[]
    >
  ) => {
    const items = action.payload
    const audioItems = items.filter(item => isAudioItem(item.type))
    const visualMediaItems = items.filter(item => isVisualMediaItem(item.type))

    state.present.multilineAudios.forEach(item => {
      const updatedItem = audioItems.find(audio => audio.id === item.id)
      if (updatedItem) {
        item.projectStart = updatedItem.left
      }
    })

    state.present.mediaItems.forEach(item => {
      const updatedItem = visualMediaItems.find(media => media.id === item.id)
      if (updatedItem) {
        item.startTime = updatedItem.left
      }
    })
  },
  addProcessingItems: (
    state,
    action: PayloadAction<(IMediaItem | IAudioItem)[]>
  ) => {
    const newItems = state.processingItems.length
      ? action.payload.filter(
          item =>
            !state.processingItems.some(
              processingItem => processingItem.id === item.id
            )
        )
      : action.payload

    if (newItems.length) {
      state.processingItems.push(...newItems)
    }
  },
  removeProcessingItems: (state, action: PayloadAction<string[]>) => {
    state.processingItems = state.processingItems.filter(
      item => !action.payload.includes(item.id)
    )
  },
  setUsedUserSoundsMetaData: (
    state,
    action: PayloadAction<TUsedUserSoundsMetaData>
  ) => {
    state.usedUserSoundsMetaData = {
      ...(state.usedUserSoundsMetaData || {}),
      ...action.payload
    }
  },
  updateAudioEffect: (
    state,
    action: PayloadAction<IUpdateAudioEffectPayload>
  ) => {
    const excludedTypes: (TMediaItemType | TAudioType)[] = [
      'image',
      'aiImage',
      'text'
    ]
    const { payload } = action
    const { effect, type, id } = payload
    if (excludedTypes.includes(type)) {
      return
    }
    const isMediaItem = isVisualMediaItem(type)
    const targetItemsList = isMediaItem
      ? state.present.mediaItems
      : state.present.multilineAudios
    const targetItem = targetItemsList.find(
      item => item.id === id && item.type === type
    )
    const effectsParent = isMediaItem
      ? (targetItem as IMediaItem).meta
      : (targetItem as IAudioItem)
    const existingEffects = effectsParent.audioEffects ?? []
    // Match on key+type (an item has at most one fade-in / fade-out), not id,
    // so repeated updates stay idempotent even if the incoming effect's id
    // differs from the stored one.
    const targetEffectIndex = existingEffects.findIndex(
      existingEffect =>
        existingEffect.key === effect.key && existingEffect.type === effect.type
    )

    if (targetEffectIndex !== -1) {
      if (effect.meta.duration === 0) {
        existingEffects.splice(targetEffectIndex, 1)
      } else {
        existingEffects[targetEffectIndex] = effect
      }
    } else {
      if (effect.meta.duration !== 0) {
        existingEffects.push(effect)
      }
    }
    effectsParent.audioEffects = existingEffects
  }
})

export default reducers
